import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, take, tap } from 'rxjs/operators';
import { CalculationModelsState, selectModels } from '../../calculation.state';
import { Store } from '@ngrx/store';
import { AppState } from '../../../app.state';
import { setAccessories, setModels, updateRainSystem } from '../../calculation.actions';
import { RoofProductTypeEnum } from 'src/api/models/roof-product-type-enum';
import { ProductsService } from '../../../../api/services/products.service';
import { ProductsMetaService } from '../../../cart/products-meta.service';
import { CoreComponent } from '../../../core.component';
import { RoofProducerEnum } from '../../../../api/models/roof-producer-enum';
import { ActivatedRoute } from '@angular/router';
import { CalculationTypeEnum } from '../../../../api/models/calculation-type-enum';

@Component({
  selector: 'roof-models',
  templateUrl: './models.component.html',
  styleUrls: ['./models.component.scss'],
})
export class ModelsComponent extends CoreComponent implements OnInit {
  form!: UntypedFormGroup;
  products!: Observable<any>;
  finishes$!: Observable<any>;
  thickness$!: Observable<any>;
  colors$!: Observable<any>;
  imagePath = '';
  modelsState!: CalculationModelsState;
  getImagePath = ProductsMetaService.getImagePath;
  colorWarrantyInYears = 0;
  corrosionWarrantyInYears = 0;
  lifetimeInYears = 0;
  h3!: string | undefined;
  private calculationType!: CalculationTypeEnum;
  @Input() productType: RoofProductTypeEnum = RoofProductTypeEnum.Tile;

  constructor(
    private productsService: ProductsService,
    private _formBuilder: UntypedFormBuilder,
    private store: Store<AppState>,
    private route: ActivatedRoute,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.h3 = this.route.snapshot.data['h3'];
    this.setupForms();
    await this.producerChange(true);
    this.garbageCollector.push(
      this.store
        .pipe(
          tap((state) => {
            this.calculationType = state.calculations.activeTab;
          }),
          map((state) => selectModels(state)),
          filter((models) => !!models?.roof.id),
          map((models) => models as CalculationModelsState),
          take(1),
        )
        .subscribe(async (models) => {
          this.form.controls.producer.patchValue(models.producer);
          await this.producerChange(true);
          this.form.controls.roof.patchValue(models.roof);
          await this.modelChange(true);
          this.form.controls.finish.patchValue(models.finish);
          await this.finishChange(true);
          this.form.controls.thickness.patchValue(models.thickness);
          await this.thicknessChange(true);
          this.form.controls.color.patchValue(models.color);
          await this.colorChange(true);
          this.form.controls.hasDripStop.patchValue(models.hasDripStop);
          this.modelsState = models;
        }),
    );
  }

  private getSelectedValue(results: any[], formControlName: string, customFindFunction?: (value: any) => any) {
    const controlValue = this.form.controls[formControlName].value;
    let value;
    if (controlValue) {
      value = results.find((result: any) => result.id === controlValue.id);
    }
    if (!value && customFindFunction) {
      value = results.find(customFindFunction);
    }

    return value ? value : results[0];
  }

  private setupForms() {
    this.form = this._formBuilder.group({
      producer: new UntypedFormControl(RoofProducerEnum.Bilka, [Validators.required]),
      roof: new UntypedFormControl('', [Validators.required]),
      finish: new UntypedFormControl('', [Validators.required]),
      thickness: new UntypedFormControl('', [Validators.required]),
      color: new UntypedFormControl('', [Validators.required]),
      hasDripStop: new UntypedFormControl(false),
    });
  }

  async producerChange(fromState = false) {
    const producer = this.form.controls.producer.value;
    if (producer && this.modelsState?.producer !== producer) {
      this.products = this.productsService
        .productsControllerGetProducts({
          producer,
          type: this.productType,
          limit: 1000,
          offset: 0,
          calculationType: this.calculationType,
        })
        .pipe(
          tap((value) => {
            const selectedValue = this.getSelectedValue(
              value.results,
              'roof',
              (product) => product.name.toLowerCase() === 'clasic',
            );
            this.form.controls.roof.patchValue(selectedValue);
            this.modelChange(true);
          }),
        );
    }
    if (!fromState) {
      this.updateState();
    }
  }
  async modelChange(fromState = false) {
    const roof = this.form.controls.roof.value;
    if (roof && this.modelsState?.roof.id !== roof.id) {
      this.finishes$ = this.productsService
        .productsControllerGetRoofFinishes({ productId: roof.id, limit: 1000, offset: 0 })
        .pipe(
          shareReplay(),
          tap((value) => {
            const selectedValue = this.getSelectedValue(
              value.results,
              'finish',
              (finish) => finish.finish.toLowerCase() === 'lucios',
            );
            this.form.controls.finish.patchValue(selectedValue);
            this.finishChange(true);
          }),
        );
    }
    if (!fromState) {
      this.updateState();
    }
  }

  async finishChange(fromState = false) {
    const roof = this.form.controls.roof.value;
    const roofFinish = this.form.controls.finish.value;
    if (roofFinish && this.modelsState?.finish?.id !== roofFinish.id) {
      this.thickness$ = this.productsService
        .productsControllerGetRoofThicknesses({ productId: roof.id, finishId: roofFinish.id, limit: 1000, offset: 0 })
        .pipe(
          shareReplay(),
          tap((value) => {
            const selectedValue = this.getSelectedValue(value.results, 'thickness');
            this.form.controls.thickness.patchValue(selectedValue);
            this.thicknessChange(true);
          }),
        );
    }
    if (!fromState) {
      this.updateState();
    }
  }

  async thicknessChange(fromState = false) {
    const roof = this.form.controls.roof.value;
    const roofFinish = this.form.controls.finish.value;
    const roofThickness = this.form.controls.thickness.value;
    if (roofThickness && this.modelsState?.thickness?.id !== roofThickness.id) {
      this.colors$ = this.productsService
        .productsControllerGetRoofColors({
          productId: roof.id,
          thicknessId: roofThickness.id,
          finishId: roofFinish.id,
          limit: 1000,
          offset: 0,
        })
        .pipe(
          shareReplay(),
          tap((value) => {
            const selectedValue = this.getSelectedValue(value.results, 'color');
            this.form.controls.color.patchValue(selectedValue);
            this.colorChange();
          }),
        );
    }
    if (!fromState) {
      this.updateState();
    }
    this.updateWarranty();
  }

  async colorChange(fromState = false) {
    if (!fromState) {
      this.updateState();
    }

    this.imagePath = this.getImagePathFromForm();
  }

  async dripstopChange() {
    this.updateState();
  }

  getImagePathFromForm(): string {
    const producer = this.form.controls.producer.value;
    const name = this.form.controls.roof.value.name;
    const finish = this.form.controls.finish.value.finish;
    const color = this.form.controls.color.value.color;
    return ProductsMetaService.getImagePath(this.productType, producer, name, finish, color);
  }

  updateState() {
    // We need to reset the accessories in case any were set on a different producer
    if (this.modelsState?.producer && this.modelsState.producer !== this.form.controls.producer.value) {
      this.store.dispatch(setAccessories({ accessories: [] }));
    }
    if (!this.modelsState?.producer || this.modelsState.producer !== this.form.controls.producer.value) {
      this.store.dispatch(
        updateRainSystem({
          producer: this.form.controls.producer.value,
          diameter: 125,
          finish: this.form.controls.producer.value === RoofProducerEnum.Bilka ? 'SSAB' : 'lucios',
          color: this.form.controls.color.value.color,
        }),
      );
    } else if (this.modelsState?.color?.color !== this.form.controls.color.value.color) {
      this.store.dispatch(
        updateRainSystem({
          color: this.form.controls.color.value.color,
        }),
      );
    }
    const models = { ...this.form.value };
    this.modelsState = models;
    this.store.dispatch(setModels({ models }));
  }

  updateWarranty(): void {
    const finish = this.form.controls.finish?.value?.finish?.toLowerCase();
    const thickness = this.form.controls.thickness?.value?.thickness;
    if (thickness < 0.4) {
      this.corrosionWarrantyInYears = this.colorWarrantyInYears = 2;
    } else if (thickness === 0.4) {
      this.corrosionWarrantyInYears = this.colorWarrantyInYears = 5;
    } else if (finish === 'lucios' || finish === 'lucios 8003') {
      this.corrosionWarrantyInYears = this.colorWarrantyInYears = 10;
    } else if (this.modelsState?.producer === RoofProducerEnum.Bilka) {
      if (finish === 'mat' || finish === 'wood') {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 15;
      } else if (finish === 'grande mat') {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 30;
      } else {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 0;
      }
    } else if (this.modelsState?.producer === RoofProducerEnum.Wetterbest) {
      if (finish === 'mat' || finish === 'wood') {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 15;
      } else if (finish === 'neomat 30') {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 30;
      } else if (finish === 'suprem 50') {
        this.corrosionWarrantyInYears = 50;
        this.colorWarrantyInYears = 25;
      } else {
        this.corrosionWarrantyInYears = this.colorWarrantyInYears = 0;
      }
    }
    this.lifetimeInYears = this.modelsState?.producer === RoofProducerEnum.Wetterbest ? 50 : 60;
  }

  getDefaultFinishColor(finish: string) {
    if (this.modelsState?.producer === RoofProducerEnum.Wetterbest) {
      return '7024';
    }
    return finish === 'lucios 8003' ? '8003' : '8017';
  }

  getFullPrice(): number {
    let price = this.form.controls.thickness.value.price;
    if (this.form.controls.hasDripStop.value) {
      price += this.form.controls.thickness.value.dripStopPrice;
    }

    return price;
  }

  canApplyDripStop(): boolean {
    const finish = this.form.controls.finish?.value?.finish?.toLowerCase();
    const canApplyDripStop: boolean =
      !finish?.includes('dublu vopsit') && (this.form.controls.roof?.value?.hasDripStop ?? false);
    if (!canApplyDripStop) {
      this.form.controls.hasDripStop.setValue(false);
    }
    return canApplyDripStop;
  }
}
